import { Behaviour } from "../utilities/ResponsiveBehaviour.js";

/**
 * Class to implement the mobile menu functionality
 * for a single instance; the application is done
 * via the MenuManager class
 */
export class MenuMobile implements Behaviour
{
    private root: HTMLElement;
    private buttons: NodeListOf<HTMLButtonElement>;
    private listeners: Array<() => void> = [];

    /**
     * The constructor
     *
     * @param {HTMLElement} root
     */
    constructor(root: HTMLElement)
    {
        this.root = root;
        this.buttons = this.root.querySelectorAll("button[aria-controls]");
    }

    /**
     * Method to apply the logic and
     * satisfy the behaviour interface
     *
     * @returns {void}
     */
    public apply(): void
    {
        this.buttons.forEach(button => {
            const menuId = button.getAttribute("aria-controls");
            const menu = menuId ? document.getElementById(menuId) : null;

            // Enable the button
            button.removeAttribute("disabled");

            // Hide the associated menu
            if (menu) {
                menu.hidden = true;
                button.setAttribute("aria-expanded", "false");
            }

            const listener = () => {
                if (!menu) return;
                const isOpen = !menu.hasAttribute("hidden");

                // Close all menus
                this.buttons.forEach(button => {
                    const menuId = button.getAttribute("aria-controls");
                    const menu = menuId ? document.getElementById(menuId) : null;
                    if (menu) {
                        menu.hidden = true;
                        button.setAttribute("aria-expanded", "false");
                    }
                });

                // If it was not open, open it now
                if (!isOpen) {
                    menu.removeAttribute("hidden");
                    button.setAttribute("aria-expanded", "true");
                }
            };

            button.addEventListener("click", listener);
            this.listeners.push(() => button.removeEventListener("click", listener));
        });
    }

    /**
     * Method to destroy the logic and
     * satisfy the behaviour interface
     *
     * @returns {void}
     */
    public destroy(): void
    {
        // Remove event listeners
        this.listeners.forEach(off => off());
        this.listeners = [];

        // Revert the state; disable buttons and
        // show menus as they originally were
        this.buttons.forEach((button) => {
            const menuId = button.getAttribute("aria-controls");
            const menu = menuId ? document.getElementById(menuId) : null;

            button.setAttribute("disabled", "");
            button.setAttribute("aria-expanded", "true");

            if (menu) menu.removeAttribute("hidden");
        });
    }
}
